import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import http from "../../api/apiClient";
import { euro, monthLabel, type Adjustment, type ContributionRow } from "./contributionsModel";

// Different contributions per child (adjustments): a lower amount, a discount or an exemption for a period.
// Set from a family; stored per child. Money from a fund is a payment (divide it), not an adjustment.

type TFn = ReturnType<typeof useTranslation>["t"];

export type AdjustmentChild = { childId: string; name: string };

/** "50% discount", "€ 100,00 per month" or "Exempt". */
export function adjustmentAmountLabel(t: TFn, adjustment: Pick<Adjustment, "kind" | "value">): string {
  const value = Number(adjustment.value);
  if ((adjustment.kind === "PERCENTAGE" && value >= 100) || (adjustment.kind === "AMOUNT" && value === 0)) {
    return t("finance.adjustments.exempt", { defaultValue: "Exempt" });
  }
  return adjustment.kind === "PERCENTAGE"
    ? t("finance.adjustments.percentage_label", { value: String(value).replace(".", ","), defaultValue: "{{value}}% discount" })
    : t("finance.adjustments.amount_label", { amount: euro(value), defaultValue: "{{amount}} per month" });
}

/** "sep 2026 – jul 2027" or "from sep 2026". */
export function adjustmentPeriodLabel(t: TFn, adjustment: Pick<Adjustment, "fromMonth" | "untilMonth">): string {
  return adjustment.untilMonth
    ? `${monthLabel(adjustment.fromMonth)} ${adjustment.fromMonth.slice(0, 4)} – ${monthLabel(adjustment.untilMonth)} ${adjustment.untilMonth.slice(0, 4)}`
    : t("finance.adjustments.from_label", { month: `${monthLabel(adjustment.fromMonth)} ${adjustment.fromMonth.slice(0, 4)}`, defaultValue: "from {{month}}" });
}

export function reasonLabel(t: TFn, reason?: Adjustment["reason"]): string {
  switch (reason) {
    case "HARDSHIP": return t("finance.adjustments.reason.hardship", { defaultValue: "Cannot afford the full fee" });
    case "STAFF": return t("finance.adjustments.reason.staff", { defaultValue: "Child of staff" });
    case "FUND": return t("finance.adjustments.reason.fund", { defaultValue: "Fund (waived part)" });
    case "OTHER": return t("finance.adjustments.reason.other", { defaultValue: "Other" });
    default: return "";
  }
}

/** "€ 300 of € 450" with the months × fee only when the child pays the normal fee all year. */
export function paidOfText(t: TFn, child: ContributionRow, monthlyFee: number | null | undefined, currentMonth?: string, excludeCurrentMonth = false): string {
  const adjusted = child.cells.some((cell) => cell.adjusted);
  if (adjusted) {
    return t("finance.adjustments.paid_of", { paid: euro(child.paid), expected: euro(child.expectedToDate), defaultValue: "{{paid}} of {{expected}}" });
  }
  const months = child.cells.filter((cell) => Number(cell.expected) > 0 && (!currentMonth || (excludeCurrentMonth ? cell.month < currentMonth : cell.month <= currentMonth))).length;
  return t("finance.contributions.child_paid_of", {
    paid: euro(child.paid),
    expected: euro(child.expectedToDate),
    months,
    fee: euro(monthlyFee ?? 0),
    defaultValue: "{{paid}} of {{expected}} ({{months}} month(s) × {{fee}})",
  });
}

/** Small violet labels for the adjustments of a child; clickable to change them when allowed. */
export function AdjustmentChips({ adjustments, canAdjust, onEdit }: {
  adjustments?: Adjustment[];
  canAdjust: boolean;
  onEdit: (adjustment: Adjustment) => void;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!adjustments || adjustments.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {adjustments.map((adjustment) => {
        const reason = reasonLabel(t, adjustment.reason);
        const text = `${adjustmentAmountLabel(t, adjustment)} · ${adjustmentPeriodLabel(t, adjustment)}${reason ? ` · ${reason}` : ""}`;
        const title = adjustment.note ? `${text}\n${adjustment.note}` : text;
        const chip = "rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-violet-100 dark:bg-violet-400/10 dark:text-violet-200 dark:ring-violet-400/20";
        return canAdjust ? (
          <button key={adjustment.id} type="button" title={title} data-adjustment={adjustment.id}
            onClick={() => onEdit(adjustment)}
            aria-label={t("finance.adjustments.edit_label", { text, defaultValue: "Change different contribution: {{text}}" })}
            className={`${chip} hover:bg-violet-100 dark:hover:bg-violet-400/20`}>
            {text}
          </button>
        ) : (
          <span key={adjustment.id} title={title} data-adjustment={adjustment.id} className={chip}>{text}</span>
        );
      })}
    </span>
  );
}

type Mode = "AMOUNT" | "PERCENTAGE" | "EXEMPT";

/**
 * Dialog to set (several children at once) or change (one existing) a different contribution.
 */
export function AdjustmentDialog({ show, title, subtitle, childOptions, defaultChildIds, existing, months, currentMonth, monthlyFee, onClose, onSaved }: {
  show: boolean;
  title: string;
  subtitle?: string;
  /** The normal fee, to show what a discount comes to. */
  monthlyFee?: number | null;
  childOptions: AdjustmentChild[];
  defaultChildIds: string[];
  /** Set to change an existing adjustment; the children cannot be changed then. */
  existing?: Adjustment | null;
  months: string[];
  currentMonth?: string;
  onClose: () => void;
  onSaved: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const lastMonth = months[months.length - 1] ?? "";
  const firstMonth = currentMonth && months.includes(currentMonth) ? currentMonth : months[0] ?? "";
  const [childIds, setChildIds] = useState<string[]>(defaultChildIds);
  const [mode, setMode] = useState<Mode>("AMOUNT");
  const [value, setValue] = useState("");
  const [fromMonth, setFromMonth] = useState(firstMonth);
  const [untilMonth, setUntilMonth] = useState(lastMonth);
  const [reason, setReason] = useState<NonNullable<Adjustment["reason"]>>("HARDSHIP");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    if (!show) return;
    setMessage(null);
    if (existing) {
      const exempt = (existing.kind === "PERCENTAGE" && Number(existing.value) >= 100) || (existing.kind === "AMOUNT" && Number(existing.value) === 0);
      setChildIds([existing.childId]);
      setMode(exempt ? "EXEMPT" : existing.kind);
      setValue(exempt ? "" : String(existing.value).replace(".", ","));
      setFromMonth(existing.fromMonth);
      setUntilMonth(existing.untilMonth ?? "");
      setReason(existing.reason ?? "OTHER");
      setNote(existing.note ?? "");
    } else {
      setChildIds(defaultChildIds);
      setMode("AMOUNT");
      setValue("");
      setFromMonth(firstMonth);
      setUntilMonth(lastMonth);
      setReason("HARDSHIP");
      setNote("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, existing]);

  const errorText = (code?: string) => {
    switch (code) {
      case "ADJUSTMENT_OVERLAP": return t("finance.adjustments.errors.overlap", { defaultValue: "This child already has a different contribution in (part of) this period. Change that one instead." });
      case "ADJUSTMENT_VALUE_INVALID": return mode === "PERCENTAGE"
        ? t("finance.adjustments.errors.percentage", { defaultValue: "Enter a percentage between 0 and 100." })
        : t("finance.adjustments.errors.amount", { defaultValue: "Enter an amount of zero or more with at most two decimals." });
      case "ADJUSTMENT_PERIOD_INVALID": return t("finance.adjustments.errors.period", { defaultValue: "The end month cannot be before the start month." });
      case "ADJUSTMENT_CHILD_REQUIRED": return t("finance.adjustments.errors.child", { defaultValue: "Choose at least one child." });
      default: return t("finance.contributions.errors.save", { defaultValue: "Saving failed. Please try again." });
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    if (childIds.length === 0) {
      setMessage(errorText("ADJUSTMENT_CHILD_REQUIRED"));
      return;
    }
    busy.current = true;
    setMessage(null);
    const body = {
      childIds,
      fromMonth,
      untilMonth: untilMonth || null,
      kind: mode === "EXEMPT" ? "PERCENTAGE" : mode,
      value: mode === "EXEMPT" ? 100 : value.replace(",", "."),
      reason,
      note: note.trim() || null,
    };
    try {
      if (existing) await http.put(`/finance/contributions/adjustments/${existing.id}`, body);
      else await http.post("/finance/contributions/adjustments", body);
      onSaved();
    } catch (caught: any) {
      setMessage(errorText(caught?.response?.data?.message));
    } finally {
      busy.current = false;
    }
  };

  const remove = async () => {
    if (!existing || busy.current) return;
    busy.current = true;
    try {
      await http.delete(`/finance/contributions/adjustments/${existing.id}`);
      onSaved();
    } catch (caught: any) {
      setMessage(errorText(caught?.response?.data?.message));
    } finally {
      busy.current = false;
    }
  };

  const monthOptions = Array.from(new Set([...months, fromMonth, untilMonth].filter(Boolean))).sort();
  const monthText = (month: string) => `${monthLabel(month)} ${month.slice(0, 4)}`;
  const field = "h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-400/20";
  const sectionLabel = "text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";
  const modes: Array<{ value: Mode; label: string }> = [
    { value: "AMOUNT", label: t("finance.adjustments.kind.amount_short", { defaultValue: "Fixed amount" }) },
    { value: "PERCENTAGE", label: t("finance.adjustments.kind.percentage_short", { defaultValue: "Discount %" }) },
    { value: "EXEMPT", label: t("finance.adjustments.kind.exempt", { defaultValue: "Exempt" }) },
  ];
  const numeric = Number(value.replace(",", "."));
  const fee = Number(monthlyFee ?? 0);
  const preview = mode === "EXEMPT"
    ? euro(0)
    : !value.trim() || Number.isNaN(numeric)
      ? null
      : mode === "AMOUNT" ? euro(numeric) : fee > 0 ? euro(Math.max(0, fee * (100 - numeric) / 100)) : null;
  const allSelected = childOptions.every((child) => childIds.includes(child.childId));

  return (
    <Modal show={show} onClose={onClose} hideDefaultActions showIcon={false} title={title}>
      <form onSubmit={submit} className="grid gap-5 text-left text-sm">
        {subtitle && <p className="-mt-2 text-center text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}

        {!existing && childOptions.length > 1 && (
          <fieldset className="grid gap-2">
            <div className="flex items-center justify-between gap-3">
              <legend className={sectionLabel}>{t("finance.payments.for_whom", { defaultValue: "For" })}</legend>
              <button type="button"
                onClick={() => setChildIds(allSelected ? [] : childOptions.map((child) => child.childId))}
                className="text-xs font-semibold text-indigo-600 hover:underline dark:text-indigo-300">
                {allSelected
                  ? t("finance.adjustments.select_none", { defaultValue: "Deselect all" })
                  : t("finance.adjustments.select_all", { defaultValue: "Select all" })}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {childOptions.map((child) => {
                const checked = childIds.includes(child.childId);
                return (
                  <label key={child.childId}
                    className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${checked
                      ? "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-400/30 dark:bg-indigo-400/15 dark:text-indigo-100"
                      : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 dark:border-white/10 dark:bg-slate-900 dark:text-slate-400"}`}>
                    <input type="checkbox" checked={checked} className="sr-only"
                      onChange={(e) => setChildIds((current) => e.target.checked
                        ? [...current, child.childId] : current.filter((id) => id !== child.childId))} />
                    <span aria-hidden="true" className={`flex h-3.5 w-3.5 items-center justify-center rounded-full text-[9px] ${checked ? "bg-indigo-600 text-white" : "border border-slate-300 dark:border-white/20"}`}>
                      {checked ? "✓" : ""}
                    </span>
                    {child.name}
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}

        <fieldset className="grid gap-2">
          <legend className={`${sectionLabel} mb-2`}>{t("finance.adjustments.kind_label", { defaultValue: "Contribution" })}</legend>
          <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5">
            {modes.map((option) => (
              <label key={option.value}
                className={`cursor-pointer rounded-lg px-2 py-2 text-center text-xs font-bold transition ${mode === option.value
                  ? "bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-white" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"}`}>
                <input type="radio" name="adjustment-kind" value={option.value} checked={mode === option.value}
                  onChange={() => setMode(option.value)} className="sr-only" />
                {option.label}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {mode !== "EXEMPT" && (
              <div className="relative w-40 shrink-0">
                {mode === "AMOUNT" && <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>}
                <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} required
                  aria-label={mode === "PERCENTAGE"
                    ? t("finance.adjustments.percentage_input", { defaultValue: "Discount in percent" })
                    : t("finance.adjustments.amount_input", { defaultValue: "Amount per month (€)" })}
                  placeholder={mode === "PERCENTAGE" ? "50" : "0,00"}
                  className={`${field} font-receipt tabular-nums ${mode === "AMOUNT" ? "pl-7" : "pr-8"}`} />
                {mode === "PERCENTAGE" && <span aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>}
              </div>
            )}
            {preview && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("finance.adjustments.preview", { amount: preview, defaultValue: "= {{amount}} per child per month" })}
              </p>
            )}
          </div>
        </fieldset>

        <fieldset className="grid gap-2">
          <legend className={`${sectionLabel} mb-2`}>{t("finance.adjustments.period", { defaultValue: "Period" })}</legend>
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
            <select value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className={field}
              aria-label={t("finance.adjustments.from", { defaultValue: "From" })}>
              {monthOptions.map((month) => <option key={month} value={month}>{monthText(month)}</option>)}
            </select>
            <span className="text-xs text-slate-400">{t("finance.adjustments.up_to", { defaultValue: "up to and incl." })}</span>
            <select value={untilMonth} onChange={(e) => setUntilMonth(e.target.value)} className={field}
              aria-label={t("finance.adjustments.until", { defaultValue: "Up to and including" })}>
              {monthOptions.map((month) => <option key={month} value={month}>{monthText(month)}</option>)}
              <option value="">{t("finance.adjustments.ongoing_short", { defaultValue: "Ongoing" })}</option>
            </select>
          </div>
          {!untilMonth && (
            <p className="text-[11px] text-slate-500">{t("finance.adjustments.ongoing_hint", { defaultValue: "Keeps applying in the next school years too." })}</p>
          )}
        </fieldset>

        <fieldset className="grid gap-2">
          <legend className={`${sectionLabel} mb-2`}>{t("finance.adjustments.reason_label", { defaultValue: "Reason" })}</legend>
          <select value={reason} onChange={(e) => setReason(e.target.value as NonNullable<Adjustment["reason"]>)} className={field}
            aria-label={t("finance.adjustments.reason_label", { defaultValue: "Reason" })}>
            {(["HARDSHIP", "STAFF", "FUND", "OTHER"] as const).map((r) => <option key={r} value={r}>{reasonLabel(t, r)}</option>)}
          </select>
          {reason === "FUND" && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-400/10 dark:text-amber-200">
              {t("finance.adjustments.fund_hint", { defaultValue: "Does the fund pay money to the school? Then it is a payment: link it to the family or divide it. Use this only for the part the school waives." })}
            </p>
          )}
          <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} className={field}
            aria-label={t("finance.adjustments.note", { defaultValue: "Note (optional, only for finance)" })}
            placeholder={t("finance.adjustments.note_placeholder", { defaultValue: "Note (optional, only for finance)" })} />
        </fieldset>

        {message && <p role="alert" className="text-sm font-semibold text-rose-600">{message}</p>}

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 dark:border-white/10">
          {existing ? (
            <button type="button" onClick={remove}
              className="h-10 rounded-lg px-3 text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-400/10">
              {t("finance.adjustments.remove", { defaultValue: "Remove (normal fee again)" })}
            </button>
          ) : (
            <p className="max-w-[16rem] text-[11px] leading-snug text-slate-400">
              {t("finance.adjustments.parent_hint", { defaultValue: "Parents only see the amount per month, not the reason or note." })}
            </p>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose}
              className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="submit" className="h-10 rounded-lg bg-indigo-600 px-5 text-sm font-bold text-white hover:bg-indigo-700">
              {t("common.save", { defaultValue: "Save" })}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
